import React, { useEffect, useState } from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { apiFetch } from "../../../../apiBindings";
import LoadingIndicator from "../../LoadingIndicator";
import HeadlineItem from "../ContentElement/HeadlineItem";
import { SideNavContainer } from "../SideNavContainer";
import useSideNavStore from "../SideNavContainer/store";

const Container = styled.div``;

const OuterContainer = styled.div`
    ${({ theme }) => css`
        ${theme.paddings.containerOuter.left}
        ${theme.paddings.containerOuter.right}
    `}
`;

const InnerContainer = styled.div`
    ${({ theme }) => css`
        ${theme.paddings.containerInner.left}
        ${theme.paddings.containerInner.right}
    `}
`;

const ChartWrapper = styled.div`
    width: 100%;
    height: 500px;
`;

const Text = styled.p`
    color: grey;
`;

const Headline = styled(HeadlineItem)``;

const shapes = ["circle", "square", "triangle", "diamond"];
const dateFromDay = (year, day) => {
    const date = new Date(year, 0);
    return new Date(date.setDate(day));
};

const HelChart = ({ name, container, text }) => {
    const theme = useTheme();
    const [paddingLeft] = useSideNavStore((state) => [state.paddingLeft]);
    const [series, setSeries] = useState([]);
    const [hiddenSeries, setHiddenSeries] = useState([]);
    const [loading, setLoading] = useState(false);
    const getColor = (name) => {
        const currentYear = new Date().getFullYear();
        const year = parseInt(name);
        if (year === currentYear) {
            return theme.colors.blue.toString();
        } else if (year === currentYear - 1) {
            return theme.colors.red.toString();
        } else if (year === currentYear - 2) {
            return theme.colors.green.toString();
        } else if (year === currentYear - 3) {
            return theme.colors.black.toString();
        } else {
            return theme.colors.darkGrey.toString();
        }
    };

    const getData = () => {
        setLoading(true);
        apiFetch("/hel-chart")
            .then((response) => response.json())
            .then((response) => {
                setSeries(
                    Object.keys(response.data)
                        .reverse()
                        .map((key, index) => {
                            return {
                                name: key,
                                color: getColor(key),
                                data: response.data[key].map((item) => {
                                    return {
                                        date: item.priceDate,
                                        value: Number(item.price),
                                    };
                                }),
                                shape: shapes[index],
                            };
                        })
                );
            })
            .finally(() => setLoading(false));
    };

    const formatMonth = (dateString) => {
        const date = dateFromDay(2024, dateString);
        return `${date.toLocaleString("default", {
            month: "long",
        })}`;
    };

    const handleClickLegend = (item) => {
        if (hiddenSeries.includes(item.value)) {
            const tempHiddenSeries = hiddenSeries.filter(
                (hidden) => hidden !== item.value
            );
            setHiddenSeries(tempHiddenSeries);
        } else {
            setHiddenSeries([...hiddenSeries, item.value]);
        }
    };

    useEffect(() => {
        getData();
    }, []);

    const component = (
        <Container>
            <SideNavContainer paddingLeft={paddingLeft}>
                {name && (
                    <Headline headline={3} color={theme.colors.red.toString()}>
                        {name}
                    </Headline>
                )}
                <ChartWrapper>
                    {loading ? (
                        <LoadingIndicator>
                            Das Preischart wird geladen. Habe bitte einen Moment
                            Geduld.
                        </LoadingIndicator>
                    ) : (
                        <ResponsiveContainer>
                            <LineChart>
                                <CartesianGrid vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    type="number"
                                    allowDuplicatedCategory={false}
                                    interval="preserveStart"
                                    includeHidden
                                    tickFormatter={formatMonth}
                                    ticks={[
                                        16, 45, 75, 105, 136, 166, 196, 226,
                                        256, 286, 316, 346,
                                    ]}
                                />
                                <YAxis
                                    dataKey="value"
                                    label={{
                                        value: "Preis in €",
                                        angle: -90,
                                        position: "insideLeft",
                                    }}
                                    domain={["auto", "auto"]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    onClick={handleClickLegend}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                {series.map((s) => (
                                    <Line
                                        stroke={s.color}
                                        activeDot={
                                            <CustomActiveDot shape={s.shape} />
                                        }
                                        dot={false}
                                        strokeWidth={2}
                                        dataKey="value"
                                        data={s.data}
                                        name={s.name}
                                        key={s.name}
                                        hide={hiddenSeries.includes(s.name)}
                                        legendType={s.shape}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </ChartWrapper>
                {text && <Text>{text}</Text>}
            </SideNavContainer>
        </Container>
    );

    if (container === "no") {
        return component;
    }

    return (
        <OuterContainer>
            {container === "small" ? (
                <InnerContainer>{component}</InnerContainer>
            ) : (
                component
            )}
        </OuterContainer>
    );
};

const CustomActiveDot = ({ cx, cy, shape, payload, stroke, ...props }) => {
    const size = 10; // Größe der Formen
    let activeDot;

    switch (shape) {
        case "circle":
            activeDot = (
                <circle
                    cx={cx}
                    cy={cy}
                    r={size}
                    strokeWidth={2}
                    stroke={stroke}
                    fill="#ffffff"
                    {...props}
                />
            );
            break;
        case "square":
            activeDot = (
                <rect
                    x={cx - 5}
                    y={cy - 5}
                    width={2 * 5}
                    height={2 * 5}
                    strokeWidth={2}
                    stroke={stroke}
                    fill="#ffffff"
                    {...props}
                />
            );
            break;
        case "triangle":
            activeDot = (
                <polygon
                    points={`${cx - size},${cy} ${cx},${cy - size} ${
                        cx + size
                    },${cy}`}
                    strokeWidth={2}
                    stroke={stroke}
                    fill="#ffffff"
                    {...props}
                />
            );
            break;
        case "diamond":
            activeDot = (
                <polygon
                    points={`${cx},${cy - 7} ${cx + 7},${cy} ${cx},${cy + 7} ${
                        cx - 7
                    },${cy}`}
                    strokeWidth={2}
                    stroke={stroke}
                    fill="#ffffff"
                    {...props}
                />
            );
            break;
        default:
            activeDot = null;
    }

    return activeDot;
};

const Label = styled.span`
    margin-bottom: 5px;
`;

const Item = styled.div`
    display: grid;
    grid-template-columns: 1fr 3fr 5fr;
    ${({ color }) => css`
        color: ${color};
    `}
`;

const TooltipContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 5px;
    background-color: ${({ theme }) => theme.colors.white.toString()};
    border: 1px solid ${({ theme }) => theme.colors.darkGrey.toString()};
`;

const CustomTooltip = ({ active, payload, label }) => {
    const currentYear = new Date().getFullYear();
    const getShape = (dataKey) => {
        if (dataKey === currentYear) {
            return "●";
        } else if (dataKey === currentYear - 1) {
            return "■";
        } else if (dataKey === currentYear - 2) {
            return "▲";
        } else if (dataKey === currentYear - 3) {
            return "♦";
        } else {
            return "";
        }
    };

    const labelFormatter = (value) => {
        const date = dateFromDay(2024, value);
        const day = date.getDate();
        return `${day < 10 ? 0 : ""}${day} ${date.toLocaleString("default", {
            month: "long",
        })}`;
    };

    const formatter = (value) => {
        return `${value.toString().replace(".", ",")} €`;
    };

    if (active && payload && payload.length) {
        return (
            <TooltipContainer>
                <Label>{labelFormatter(label)}</Label>
                {payload.map((item) => {
                    return (
                        <Item color={item.color} key={item.name}>
                            <div>{getShape(parseInt(item.name))}</div>
                            <div>{item.name}: </div>
                            <div>{formatter(item.value)}</div>
                        </Item>
                    );
                })}
            </TooltipContainer>
        );
    }

    return null;
};
export default HelChart;
